import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { DateAgoPipe } from '../../../../shared/pipes/date-ago.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { MigrationsCucmHuntListsContext } from '../../../services/cisco/migrations-cucm-hunt-lists.context';
import {
  CiscoPbxExport,
  CucmHuntList,
  CucmLineGroupMembership,
  JobState,
} from '../../../../shared/models/generated/ziroModels';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { Subscription } from 'rxjs';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';

interface LineGroupDatatableRow extends DatatableRow {
  directoryNumber: string;
  description: string;
  alertingName: string;
}

interface LineGroupInfo {
  lineGroup: CucmLineGroupMembership;
  cardHeader: string;
  data: LineGroupDatatableRow[];
}

@Component({
  selector: 'ziro-migrations-cisco-hunt-lists-report-line-groups',
  templateUrl: 'migrations-cisco-hunt-lists-report-line-groups.component.html',
  styleUrls: ['migrations-cisco-hunt-lists-report-line-groups.component.scss', '../../../migrations.component.scss'],
  standalone: false,
})
export class MigrationsCiscoHuntListsReportLineGroupsComponent implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsCucmHuntListsContext = inject(MigrationsCucmHuntListsContext);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);

  isLoading = true;
  huntList: CucmHuntList;
  lineGroupColumns: DatatableColumn<LineGroupDatatableRow>[] = [
    {
      name: 'directoryNumber',
      label: 'tkey;migrations.cisco.hunt_lists.line_groups.table.dn',
      pipe: {
        transform: (value: string): string => {
          return `<strong>${this._telephoneNumberFilter.transform(value)}</strong>`;
        },
      },
      columnCharacterWidth: 20,
    },
    {
      name: 'description',
      label: 'tkey;migrations.cisco.hunt_lists.line_groups.table.description',
      columnCharacterWidth: 60,
    },
    {
      name: 'alertingName',
      label: 'tkey;migrations.cisco.hunt_lists.line_groups.table.alerting_name',
      columnCharacterWidth: 30,
    },
  ];
  lineGroups: LineGroupInfo[] = [];

  ZiroIcons = ZiroIcons;
  isCdrPresent = false;

  private _assessmentId: string;
  private _tenantId: string;
  private _huntListName: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._initBottomNav();
    this._assessmentId = this._route.snapshot.params['id'];
    this._tenantId = this._route.snapshot.params['tenantId'];
    this._huntListName = this._route.snapshot.params['name'];

    const exportSub = this._migrationsAssessmentsContext.state$.subscribe((migrations) => {
      const assessment = migrations.ciscoPbxExports.find(
        (migration: CiscoPbxExport) => migration.id === this._assessmentId,
      );
      this.isCdrPresent = assessment.isCucmCdrDumpPresent;
      this._initBreadcrumbs(assessment.name);
    });
    this._subscriptions.add(exportSub);

    const huntListSub = this._migrationsCucmHuntListsContext.state$.subscribe((report) => {
      this.huntList = report.find((huntList) => huntList.name === this._huntListName);
      if (!this.huntList) {
        throw new Error(`Could not find hunt list with name ${this._huntListName}`);
      }
      this.lineGroups = this.huntList.lineGroupMemberships
        .sort((a, b) => a.selectionOrder - b.selectionOrder)
        .map((membership) => this._mapLineGroupInfo(membership));
      this.isLoading = false;
    });
    this._subscriptions.add(huntListSub);

    const huntListStatusSub = this._migrationsCucmHuntListsContext.jobState$.subscribe((status) => {
      if (status.lastRunTime) {
        this._updateBottomNavTimestamp(status.lastRunTime);
      }
      const isRefreshing = !status.lastRunTime || status.jobState !== JobState.IDLE;
      this._setRefreshButtonPendingState(isRefreshing);
    });
    this._subscriptions.add(huntListStatusSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  getNumberOfMembers(lineGroupMemberships: CucmLineGroupMembership[]): number {
    const directoryNumbers = lineGroupMemberships.flatMap((membership) =>
      membership.lineGroupMembers.map((member) => `${member.extension}_${member.routePartition}`),
    );
    return new Set(directoryNumbers).size;
  }

  getNumberOfLineGroups(lineGroupMemberships: CucmLineGroupMembership[]): number {
    return lineGroupMemberships.length;
  }

  private _mapLineGroupInfo(lineGroup: CucmLineGroupMembership): LineGroupInfo {
    return {
      cardHeader: this._translateService.instant(
        'tkey;migrations.cisco.hunt_lists.line_groups.line_group_details.header',
        { lineGroupName: lineGroup.lineGroupName },
      ),
      lineGroup: lineGroup,
      data: lineGroup.lineGroupMembers
        .sort((a, b) => a.lineSelectionOrder - b.lineSelectionOrder)
        .map((member) => {
          return {
            directoryNumber: member.extension,
            description: member.description,
            alertingName: member.alertingName,
          };
        }),
    };
  }

  private _onRefreshClick(): void {
    this.isLoading = true;
    this._migrationsCucmHuntListsContext.refresh().subscribe();
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'cucm-hunt-lists-report-refresh',
          dataAutomation: 'migrations-cisco-hunt-lists-refresh-button',
          label: 'tkey;migrations.cisco.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.DEFAULT,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this._onRefreshClick(),
        },
        {
          id: 'cucm-hunt-lists-report-back',
          dataAutomation: 'migrations-cisco-hunt-lists-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => {
            this._router.navigate(['..'], { relativeTo: this._route });
          },
        },
      ]),
    );
  }

  private _updateBottomNavTimestamp(timestamp: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.dial_plan_inventory.last_updated',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _setRefreshButtonPendingState(state: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'cucm-hunt-lists-report-refresh',
        state: {
          pending: state,
          buttonDisableState: { disabled: state, tooltipKey: '' },
        },
      }),
    );
  }

  private _initBreadcrumbs(migrationName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: migrationName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this._assessmentId}`,
      },
      {
        label: 'tkey;migrations.cisco.reports_list.table.hunt_lists',
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this._assessmentId}/cucm-hunt-lists-report`,
      },
      { label: this._huntListName },
    ]);
  }
}
